import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Play, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { imgUrl, imgUrlW } from "@/lib/api";
import { EASE } from "@/components/luxe/Reveal";
import { TikTokIcon } from "@/components/luxe/icons";

const cleanTitle = (t = "") => t.replace(/#\S+/g, "").replace(/\s+/g, " ").trim() || "Watch on TikTok";

export const TikTokShowcase = ({ items = [] }) => {
  const reduce = useReducedMotion();
  const [current, setCurrent] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const openItem = (it) => {
    if (!it.video_id) {
      window.open(it.url, "_blank", "noopener,noreferrer");
      return;
    }
    setLoaded(false);
    setCurrent(it);
  };

  return (
    <>
      <div className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-6 sm:overflow-visible sm:px-0 lg:grid-cols-3">
        {items.map((it, i) => (
          <motion.button
            type="button"
            key={it.id || i}
            data-testid="tiktok-video-card"
            onClick={() => openItem(it)}
            initial={reduce ? false : { opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
            className="group relative w-[72%] shrink-0 snap-start overflow-hidden rounded-2xl border border-[hsl(345_35%_40%/0.5)] bg-[hsl(345_50%_16%)] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-auto"
          >
            <div className="relative aspect-[9/14] overflow-hidden">
              {it.thumbnail ? (
                <img src={imgUrlW(it.thumbnail, 640)} alt={cleanTitle(it.title)} loading="lazy" className="h-full w-full object-cover transition-transform [transition-duration:1200ms] ease-out group-hover:scale-[1.05]" />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-[hsl(345_45%_18%)]">
                  <TikTokIcon className="h-10 w-10 text-[hsl(var(--gold))]" />
                </div>
              )}
              <span className="absolute inset-0 bg-[linear-gradient(to_top,rgba(16,11,8,0.85),rgba(16,11,8,0.05)_55%)]" />
              <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[hsl(var(--gold)/0.8)] bg-[hsl(345_55%_9%/0.45)] text-[hsl(350_60%_97%)] backdrop-blur-sm transition-transform duration-500 group-hover:scale-110">
                <Play className="ml-0.5 h-5 w-5 fill-current" />
              </span>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <span className="eyebrow text-[10px] text-[hsl(var(--gold))]">No. {String(i + 1).padStart(2, "0")}</span>
                <p className="mt-2 line-clamp-2 font-display text-lg leading-snug text-[hsl(350_60%_97%)]">{cleanTitle(it.title)}</p>
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      <Dialog open={!!current} onOpenChange={(v) => !v && setCurrent(null)}>
        <DialogContent data-testid="tiktok-embed-dialog" className="on-dark w-[min(92vw,380px)] max-w-none gap-0 overflow-hidden border-none bg-[hsl(345_55%_9%)] p-0 text-[hsl(350_60%_97%)] sm:rounded-2xl [&>button]:right-3 [&>button]:top-3 [&>button]:z-10 [&>button]:rounded-full [&>button]:bg-[hsl(345_55%_9%/0.8)] [&>button]:p-1.5 [&>button]:text-[hsl(350_60%_97%)] [&>button]:opacity-100">
          <DialogTitle className="sr-only">{current ? cleanTitle(current.title) : "TikTok"}</DialogTitle>
          <DialogDescription className="sr-only">TikTok video</DialogDescription>
          {current && (
            <>
              <div className="relative h-[min(72vh,680px)] w-full">
                {!loaded && <Skeleton className="absolute inset-0 rounded-none bg-[hsl(345_40%_18%)]" />}
                <iframe
                  title={cleanTitle(current.title)}
                  src={`https://www.tiktok.com/embed/v2/${current.video_id}`}
                  className="h-full w-full"
                  allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen
                  onLoad={() => setLoaded(true)}
                />
              </div>
              <a
                data-testid="tiktok-embed-open-link"
                href={current.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 border-t border-[hsl(345_35%_40%/0.4)] px-5 py-3.5 text-xs uppercase tracking-[0.24em] text-[hsl(345_40%_86%)] transition-colors hover:text-[hsl(var(--gold))]"
              >
                Open in TikTok <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
